export interface PaletteCommand {
  label: string;
  hint?: string;
  action: () => void;
}

let idCounter = 0;

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || (el as HTMLElement).isContentEditable;
}

interface UserAgentDataLike {
  platform?: string;
}

/** Mac uses ⌘, everything else Ctrl - the header hint should match the key the visitor actually presses. */
export function isMacPlatform(nav: Navigator = navigator): boolean {
  const modern = (nav as Navigator & { userAgentData?: UserAgentDataLike }).userAgentData?.platform;
  const platform = modern || nav.platform || nav.userAgent || "";
  return /mac|iphone|ipad|ipod/i.test(platform);
}

function applyShortcutLabel(): void {
  if (!isMacPlatform()) return;
  const label = document.getElementById("paletteTriggerLabel");
  if (label) label.textContent = "⌘K";

  const trigger = document.getElementById("paletteTrigger");
  trigger?.setAttribute("aria-label", "Befehlspalette öffnen (Cmd+K)");
  trigger?.setAttribute("title", "Befehlspalette (Cmd+K)");
}

function defaultCommands(): PaletteCommand[] {
  const goTo =
    (path: string): (() => void) =>
    () =>
      window.location.assign(path);
  const openExternal =
    (url: string): (() => void) =>
    () =>
      window.open(url, "_blank", "noopener");

  return [
    { label: "Startseite", hint: "/", action: goTo("/") },
    { label: "Über uns", hint: "/ueber-uns.html", action: goTo("/ueber-uns.html") },
    { label: "Ausstattung", hint: "/ausstattung.html", action: goTo("/ausstattung.html") },
    { label: "Termine", hint: "/termine.html", action: goTo("/termine.html") },
    { label: "Galerie", hint: "/galerie.html", action: goTo("/galerie.html") },
    { label: "Kontakt", hint: "/kontakt.html", action: goTo("/kontakt.html") },
    { label: "Impressum", hint: "/impressum.html", action: goTo("/impressum.html") },
    { label: "Datenschutz", hint: "/datenschutz.html", action: goTo("/datenschutz.html") },
    { label: "Matrix Chat öffnen", hint: "↗", action: openExternal("https://matrix.to/#/#space:aalen.space") },
    { label: "Nach oben scrollen", action: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
  ];
}

/**
 * A VS-Code/Spotlight-style command palette: Ctrl+K / Cmd+K (or "/" outside of a text
 * field) opens a searchable list of pages and quick actions. Returns a dispose function
 * that removes all listeners and the injected DOM.
 */
export function initCommandPalette(commands: PaletteCommand[] = defaultCommands()): () => void {
  const overlay = document.createElement("div");
  overlay.className = "command-palette-overlay";

  const box = document.createElement("div");
  box.className = "command-palette";
  box.setAttribute("role", "dialog");
  box.setAttribute("aria-modal", "true");
  box.setAttribute("aria-label", "Befehlspalette");

  const input = document.createElement("input");
  input.type = "text";
  input.className = "command-palette-input";
  input.placeholder = "Seite oder Befehl suchen…";
  input.setAttribute("aria-label", "Seite oder Befehl suchen");
  input.autocomplete = "off";
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-expanded", "false");
  input.setAttribute("aria-autocomplete", "list");

  idCounter += 1;
  const list = document.createElement("ul");
  list.id = `command-palette-listbox-${idCounter}`;
  list.className = "command-palette-list";
  list.setAttribute("role", "listbox");
  list.setAttribute("aria-label", "Seiten und Befehle");
  input.setAttribute("aria-controls", list.id);

  box.append(input, list);
  overlay.append(box);
  document.body.append(overlay);

  let filtered: PaletteCommand[] = commands;
  let activeIndex = 0;
  let lastFocused: HTMLElement | null = null;

  function renderList(): void {
    list.replaceChildren();
    if (filtered.length === 0) {
      input.removeAttribute("aria-activedescendant");
      list.scrollTop = 0;
      const empty = document.createElement("li");
      empty.className = "command-palette-empty";
      empty.textContent = "Keine Ergebnisse";
      list.append(empty);
      return;
    }

    filtered.forEach((command, index) => {
      const item = document.createElement("li");
      item.id = `${list.id}-option-${index}`;
      item.className = "command-palette-item";
      item.setAttribute("role", "option");
      item.setAttribute("aria-selected", String(index === activeIndex));
      if (index === activeIndex) {
        item.classList.add("is-active");
        input.setAttribute("aria-activedescendant", item.id);
      }

      const label = document.createElement("span");
      label.textContent = command.label;
      item.append(label);

      if (command.hint) {
        const hint = document.createElement("span");
        hint.className = "command-palette-hint";
        hint.textContent = command.hint;
        item.append(hint);
      }

      item.addEventListener("mouseenter", () => {
        activeIndex = index;
        renderList();
      });
      item.addEventListener("click", () => {
        activeIndex = index;
        runActive();
      });

      list.append(item);
    });

    // Every render replaces all <li>s, so the scroll container keeps whatever
    // scrollTop it had before — without this, a filtered/re-ordered result set
    // (or the highlight moving via Arrow keys) can render entirely outside the
    // still-scrolled viewport, looking like the palette "shows nothing".
    const activeItem = list.children[activeIndex];
    if (activeItem && typeof activeItem.scrollIntoView === "function") {
      activeItem.scrollIntoView({ block: "nearest" });
    }
  }

  function runActive(): void {
    const command = filtered[activeIndex];
    if (!command) return;
    close();
    command.action();
  }

  function filterCommands(): void {
    const query = input.value.trim().toLowerCase();
    filtered = query
      ? commands.filter(
          (c) => c.label.toLowerCase().includes(query) || (c.hint?.toLowerCase().includes(query) ?? false),
        )
      : commands;
    activeIndex = 0;
    renderList();
  }

  function open(): void {
    lastFocused = document.activeElement as HTMLElement | null;
    overlay.classList.add("is-open");
    // Stop the page behind the modal from scrolling along with it.
    document.body.classList.add("has-palette-open");
    input.setAttribute("aria-expanded", "true");
    input.value = "";
    filterCommands();
    input.focus();
  }

  function close(): void {
    overlay.classList.remove("is-open");
    document.body.classList.remove("has-palette-open");
    input.setAttribute("aria-expanded", "false");
    lastFocused?.focus();
  }

  function isOpen(): boolean {
    return overlay.classList.contains("is-open");
  }

  const handleGlobalKeydown = (event: KeyboardEvent): void => {
    const isOpenShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
    const isSlashShortcut = event.key === "/" && !isTypingTarget(document.activeElement);

    if (isOpenShortcut || isSlashShortcut) {
      event.preventDefault();
      if (isOpen()) {
        close();
      } else {
        open();
      }
    }
  };

  const handleInputKeydown = (event: KeyboardEvent): void => {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        close();
        break;
      case "Tab":
        // The input is the only focusable element in this dialog (list items aren't
        // tabbable, arrow keys handle that navigation instead) - without this, Tab would
        // move focus to whatever's behind the modal overlay while it's still shown.
        event.preventDefault();
        break;
      case "ArrowDown":
        event.preventDefault();
        if (filtered.length > 0) activeIndex = (activeIndex + 1) % filtered.length;
        renderList();
        break;
      case "ArrowUp":
        event.preventDefault();
        if (filtered.length > 0) activeIndex = (activeIndex - 1 + filtered.length) % filtered.length;
        renderList();
        break;
      case "Enter":
        event.preventDefault();
        runActive();
        break;
    }
  };

  const handleOverlayClick = (event: MouseEvent): void => {
    if (event.target === overlay) close();
  };

  const handleTriggerClick = (): void => {
    if (isOpen()) {
      close();
    } else {
      open();
    }
  };

  document.addEventListener("keydown", handleGlobalKeydown);
  input.addEventListener("keydown", handleInputKeydown);
  input.addEventListener("input", filterCommands);
  overlay.addEventListener("click", handleOverlayClick);

  const trigger = document.getElementById("paletteTrigger");
  trigger?.addEventListener("click", handleTriggerClick);

  applyShortcutLabel();

  return () => {
    document.removeEventListener("keydown", handleGlobalKeydown);
    trigger?.removeEventListener("click", handleTriggerClick);
    document.body.classList.remove("has-palette-open");
    overlay.remove();
  };
}
